"use client";

import { signal } from "@preact/signals-react";
import { useSignals } from "@preact/signals-react/runtime";
import { useQuery } from "@tanstack/react-query";
import type { GetAppStateResponse } from "deso-protocol";
import { getAppState } from "deso-protocol";
import type { FC, MutableRefObject, ReactNode } from "react";
import {
  createContext,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
} from "react";

import { getCoinPriceInUsd } from "@/backend/deso";
import { useIdleReady } from "@/hooks/use-idle-ready";
import {
  FOCUS_MARKET_DATA_CACHE_MAX_AGE_MS,
  FOCUS_MARKET_DATA_QUERY_KEY,
} from "@/utils/query-persistence";

type FocusMarketDataSnapshot = {
  appState: GetAppStateResponse;
  focusExchangeRate: number | null;
};

interface FocusMarketDataContextType {
  appState: GetAppStateResponse | null;
  currentBonus: number;
  focusExchangeRate: number | null;
  loading: boolean;
  refreshExchangeRates: (
    loading?: boolean,
  ) => Promise<FocusMarketDataSnapshot | undefined>;
  exchangeRatesLoadingPromise: MutableRefObject<
    Promise<FocusMarketDataSnapshot | undefined> | undefined
  >;
}

const FocusMarketDataContext = createContext<FocusMarketDataContextType>({
  appState: null,
  currentBonus: 0,
  focusExchangeRate: null,
  loading: false,
  refreshExchangeRates: () => Promise.resolve(undefined),
  exchangeRatesLoadingPromise: { current: undefined },
});

type FocusMarketDataSignalState = Pick<
  FocusMarketDataContextType,
  "appState" | "currentBonus" | "focusExchangeRate" | "loading"
>;

const defaultFocusMarketDataState: FocusMarketDataSignalState = {
  appState: null,
  currentBonus: 0,
  focusExchangeRate: null,
  loading: true,
};

export const focusMarketDataSignal = signal<FocusMarketDataSignalState>({
  ...defaultFocusMarketDataState,
});

let refreshExchangeRatesSignalBridge: FocusMarketDataContextType["refreshExchangeRates"] =
  () => Promise.resolve(undefined);
let exchangeRatesLoadingPromiseSignalBridge: FocusMarketDataContextType["exchangeRatesLoadingPromise"] =
  { current: undefined };

export const useFocusMarketData = (): FocusMarketDataContextType => {
  useSignals();

  return {
    ...focusMarketDataSignal.value,
    exchangeRatesLoadingPromise: exchangeRatesLoadingPromiseSignalBridge,
    refreshExchangeRates: refreshExchangeRatesSignalBridge,
  };
};

type FocusMarketDataContextProps = {
  children?: ReactNode;
};

const getFocusMarketData = async (): Promise<FocusMarketDataSnapshot> => {
  const [appStateResponse, { data }] = await Promise.all([
    getAppState(),
    getCoinPriceInUsd(process.env.NEXT_PUBLIC_FOCUS_APP_PUBLIC_KEY || ""),
  ]);

  const focusExchangeRate =
    data.MidPrice && Number.isFinite(Number(data.MidPrice))
      ? Number.parseFloat(data.MidPrice)
      : null;

  return {
    appState: appStateResponse as GetAppStateResponse,
    focusExchangeRate,
  };
};

const FocusMarketDataProvider: FC<FocusMarketDataContextProps> = ({
  children,
}) => {
  const exchangeRatesLoadingPromise =
    useRef<Promise<FocusMarketDataSnapshot | undefined>>(undefined);
  const idleExchangeRatesReady = useIdleReady(true, {
    timeout: 1200,
  });

  const {
    data: marketData,
    isFetching,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: [FOCUS_MARKET_DATA_QUERY_KEY],
    queryFn: getFocusMarketData,
    enabled: idleExchangeRatesReady,
    staleTime: FOCUS_MARKET_DATA_CACHE_MAX_AGE_MS,
    gcTime: FOCUS_MARKET_DATA_CACHE_MAX_AGE_MS,
    refetchOnWindowFocus: false,
  });

  const refreshExchangeRates = useCallback(async () => {
    const promise = refetch().then((result) => result.data);
    exchangeRatesLoadingPromise.current = promise;
    return promise;
  }, [refetch]);

  const loading =
    !marketData && (!idleExchangeRatesReady || isLoading || isFetching);
  const signalState = useMemo(
    () => ({
      appState: marketData?.appState ?? null,
      loading,
      focusExchangeRate: marketData?.focusExchangeRate ?? null,
      currentBonus: 0,
    }),
    [loading, marketData?.appState, marketData?.focusExchangeRate],
  );

  useLayoutEffect(() => {
    focusMarketDataSignal.value = signalState;
    refreshExchangeRatesSignalBridge = refreshExchangeRates;
    exchangeRatesLoadingPromiseSignalBridge = exchangeRatesLoadingPromise;

    return () => {
      refreshExchangeRatesSignalBridge = () => Promise.resolve(undefined);
      exchangeRatesLoadingPromiseSignalBridge = { current: undefined };
    };
  }, [exchangeRatesLoadingPromise, refreshExchangeRates, signalState]);

  return (
    <FocusMarketDataContext.Provider
      value={{
        ...signalState,
        refreshExchangeRates,
        exchangeRatesLoadingPromise,
      }}
    >
      {children}
    </FocusMarketDataContext.Provider>
  );
};

export { FocusMarketDataContext, FocusMarketDataProvider };
